import React, { useCallback, useMemo, useState } from "react";
import { FiRefreshCcw, FiFilter } from "react-icons/fi";
import { useInvalidate } from "../../../../hooks/useInvalidate";
import Divider from "../../../../components/shared/Divider/Divider";
import { Pagination } from "../../../../components/shared/Pagination/Pagination";
import Button from "../../../../components/shared/Button/Button";
import SelectMulti from "../../../../components/shared/Select/SelectMulti";
import { useAdminSearchStore } from "../../store/adminSearchStore";
import { useAdminSearchInvites } from "../../hooks/useAdminSearchInvites";
import { InvitesFiltersPanel } from "./InvitesFiltersPanel";
import { createInvitesColumnDefinitions } from "./InvitesTableColumns";
import {
  INVITES_FIXED_COLUMNS,
  invitesColumnOptions,
} from "./constants/columns";
import EditInviteModal from "./EditInviteModal";

export default function InvitesView() {
  const invalidate = useInvalidate();
  const {
    invitesState,
    setInvitesPagination,
    setInvitesVisibleColumns,
    setInvitesFiltersOpen,
    setSelectedInviteId,
  } = useAdminSearchStore();

  const { filters, pagination, visibleColumns, filtersOpen, selectedInviteId } =
    invitesState;

  const { data, isLoading, error } = useAdminSearchInvites();
  const invites = data?.invites || [];
  const total = data?.total || 0;
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / (pagination.limit || 1))),
    [total, pagination.limit]
  );

  const onPageChange = useCallback(
    async (page) => {
      const clamped = Math.min(Math.max(page, 1), totalPages);
      setInvitesPagination({ page: clamped });
    },
    [totalPages, setInvitesPagination]
  );

  const onRowsPerPageChange = useCallback(
    async (n) => {
      setInvitesPagination({ page: 1, limit: n });
    },
    [setInvitesPagination]
  );

  const onRefresh = useCallback(() => {
    invalidate(["adminSearchInvites_key"]);
  }, [invalidate]);

  const handleEditInvite = useCallback(
    (inviteId) => {
      setIsEditingModalOpen(true);
      setSelectedInviteId(inviteId);
    },
    [setSelectedInviteId]
  );

  const handleCloseEdit = useCallback(() => {
    setIsEditingModalOpen(false);
    setSelectedInviteId(null);
  }, [setSelectedInviteId]);

  const handleFiltersSubmit = useCallback(() => {
    setInvitesPagination({ page: 1 });
    setInvitesFiltersOpen(false);
    invalidate(["adminSearchInvites_key"]);
  }, [invalidate, setInvitesFiltersOpen, setInvitesPagination]);

  // Column definitions
  const columnDefinitions = useMemo(() => createInvitesColumnDefinitions(), []);

  const columnHandlers = useMemo(
    () => ({
      onEditInvite: handleEditInvite,
    }),
    [handleEditInvite]
  );

  const visibleColumnDefs = useMemo(() => {
    // Always include the fixed columns first
    const fixedCols = INVITES_FIXED_COLUMNS.map(
      (colKey) => columnDefinitions[colKey]
    ).filter(Boolean);
    const otherCols = visibleColumns
      .filter(
        (colKey) =>
          !INVITES_FIXED_COLUMNS.includes(colKey) && columnDefinitions[colKey]
      )
      .map((colKey) => columnDefinitions[colKey]);
    // Return fixed columns first, then other selected columns
    return [...fixedCols, ...otherCols];
  }, [visibleColumns, columnDefinitions]);

  const columnCount = visibleColumnDefs.length || 1;

  const hasActiveFilters = useMemo(() => {
    const { search, invitee, senderName, inviteeName, status } = filters;
    return Boolean(
      (search || "").trim() || invitee || senderName || inviteeName || status
    );
  }, [filters]);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={() => setInvitesFiltersOpen(true)}
          color={hasActiveFilters ? "primary50" : "white/gray300"}
          size="md"
          rounded="lg"
          startContent={<FiFilter />}
          title="Open filters"
        >
          Filters
        </Button>
        <SelectMulti
          header="Columns"
          className="min-w-[192px]"
          onChange={(val) => {
            if (INVITES_FIXED_COLUMNS.includes(val)) return;
            setInvitesVisibleColumns((prev = INVITES_FIXED_COLUMNS) => {
              const filtered = prev.filter(
                (v) => !INVITES_FIXED_COLUMNS.includes(v)
              );
              return filtered.includes(val)
                ? filtered.filter((v) => v !== val)
                : [...filtered, val];
            });
          }}
          onClear={() => setInvitesVisibleColumns([...INVITES_FIXED_COLUMNS])}
          options={invitesColumnOptions}
          placeholder="Select columns..."
          values={(visibleColumns || []).filter(
            (v) => !INVITES_FIXED_COLUMNS.includes(v)
          )}
          buttonProps={{
            size: "md",
            rounded: "lg",
            color:
              (visibleColumns || []).filter(
                (v) => !INVITES_FIXED_COLUMNS.includes(v)
              ).length > 0
                ? "primary50"
                : "white/gray300",
          }}
        />
        <Button
          type="button"
          isDisabled={isLoading}
          onClick={onRefresh}
          color="white/gray300"
          size="md"
          rounded="lg"
          startContent={<FiRefreshCcw />}
          title="Refresh"
        >
          Refresh
        </Button>
      </div>

      <InvitesFiltersPanel
        isOpen={filtersOpen}
        onClose={() => setInvitesFiltersOpen(false)}
        onSubmit={handleFiltersSubmit}
      />

      <div className="overflow-hidden rounded-xl border border-gray200">
        <div className="overflow-x-auto min-h-80">
          <table className="min-w-full text-left text-base">
            <thead className="bg-gray50 text-sm uppercase tracking-wide text-gray600">
              <tr>{visibleColumnDefs.map((col) => col.renderHeader())}</tr>
            </thead>

            <tbody className="divide-y divide-gray200">
              {error && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-red-700"
                  >
                    {error?.message || "Failed to fetch users"}
                  </td>
                </tr>
              )}
              {isLoading && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-gray500"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && invites.length === 0 && !error && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-gray500"
                  >
                    No results
                  </td>
                </tr>
              )}

              {invites.map((i, index) => {
                return (
                  <tr key={i.id} className={index % 2 === 1 ? "bg-gray50" : ""}>
                    {visibleColumnDefs.map((col) => (
                      <React.Fragment key={col.key}>
                        {col.renderCell(i, columnHandlers)}
                      </React.Fragment>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Divider color="gray200" type="horizontal" />

        <Pagination
          currentPage={pagination.page}
          pageNumber={totalPages}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPage={pagination.limit}
          showItemsPerPage={invites.length > 0}
          size="lg"
          className="px-6 py-4"
        />
      </div>

      <EditInviteModal isOpen={isEditingModalOpen} onClose={handleCloseEdit} />
    </>
  );
}
